/**
 * Arrangementets Overview — «beredskapsdashbord. Ikke en kopi av den personlige Overview — dette
 * svarer på: er vi klare?» (PAGES.md, [F2]).
 *
 * COUNTED BY THE SERVER, and that is not an optimisation. A client that counts the page it is
 * holding gives a number that is right about fifty rows and wrong about the festival — the same
 * argument grouping makes on the task list. And the counts are narrowed by the SAME scope the
 * lists are, so a `limited` user is never told that fifty tasks exist when they may see three.
 */
import { z } from 'zod';

import { performanceStatusSchema } from '../enums.js';

/**
 * One concrete thing that is not ready, with the id needed to open it.
 *
 * `at` AND `status` RATHER THAN A FORMATTED `detail`, which is what this was until QA runde 4:
 * the server wrote «2027-07-08 10:00» in UTC for a shift that starts 12:00 in Oslo, and
 * «verbally_confirmed» where the reader should see «Muntlig bekreftet». The zone and the words
 * belong to the reader.
 */
export const readinessGapSchema = z
  .object({
    kind: z.enum(['performance', 'shift', 'task']),
    id: z.uuid(),
    /** Null for a booking with no artist, no register row and no title. */
    label: z.string().nullable(),
    /** A task's deadline or a shift's start. */
    at: z.iso.datetime().nullable(),
    /** A booking's place in the contract chain; null for the other kinds. */
    status: performanceStatusSchema.nullable(),
  })
  .meta({ id: 'ReadinessGap' });

/** The most gaps one dashboard shows. Past this it is a report, and the page links to the list. */
export const MAX_READINESS_GAPS = 12;

export const editionReadinessSchema = z
  .object({
    editionId: z.uuid(),
    startsOn: z.iso.date().nullable(),
    /** Whole days from today to the first day, in the ORGANISATION's zone. Negative once open. */
    daysUntil: z.number().int().nullable(),
    performances: z.object({
      total: z.number().int().nonnegative(),
      /** Signed, announced or performed: the contract chain has reached certainty. */
      /** Null for a guest (239.31): the count is contract status. */
      confirmed: z.number().int().nonnegative().nullable(),
      /** Has an advance row with an arrival time — «påbegynt», never «fullført». See the page. */
      advanceStarted: z.number().int().nonnegative(),
    }),
    shifts: z.object({
      total: z.number().int().nonnegative(),
      understaffed: z.number().int().nonnegative(),
      slotsNeeded: z.number().int().nonnegative(),
      slotsFilled: z.number().int().nonnegative(),
    }),
    tasks: z.object({
      overdueOpen: z.number().int().nonnegative(),
    }),
    gaps: z.array(readinessGapSchema),
  })
  .meta({ id: 'EditionReadiness' });

export type ReadinessGap = z.infer<typeof readinessGapSchema>;
export type EditionReadiness = z.infer<typeof editionReadinessSchema>;
